import { getFormatMessage } from '@/utils/utils'
import { ApiOutlined, StarOutlined, UsbOutlined } from '@ant-design/icons'
import {
  Button,
  Checkbox,
  Dropdown,
  List,
  Menu,
  Radio,
  Space,
  Tooltip
} from 'antd'
import React, { ReactNode, useEffect, useState } from 'react'
import { BoxItem, BoxListProps } from '../interface'
import styles from './index.css'

export const BoxList: React.FC<BoxListProps> = (props: BoxListProps) => {
  const {
    data,
    selectable,
    selectionType,
    checkAll,
    checkedItems,
    onCheck,
    onSelect,
    onMenuItemClick,
    onFavorite,
    onImport
  } = props
  const [selectItem, setSelectItem] = useState<BoxItem | undefined>()
  // const [selected, setSelected] = useState<BoxItem[]>([])
  const formatMessage = getFormatMessage()

  const handleChange = (checked: boolean, item: BoxItem) => {
    let items: BoxItem[] = []
    if (checked) {
      items = [...checkedItems!]
      items.push(item)
    } else {
      items = checkedItems!.filter((x) => x.id !== item.id)
    }
    onCheck?.(items)
  }

  const header = (
    <div className={styles.header}>
      <div className={styles.title}>
        {formatMessage({ id: 'app.box.list.title' })}
      </div>
      <Space
        style={{ flex: 'auto', justifyContent: 'flex-end' }}
        direction='horizontal'
      >
        <Button type='primary' onClick={onImport}>
          {formatMessage({ id: 'app.box.toolbar.import' })}
        </Button>
      </Space>
    </div>
  )

  const avatar = (item: BoxItem) => {
    if (selectable) {
      if (selectionType === 'checkbox') {
        return (
          <Checkbox
            onClick={(e) => {
              e.stopPropagation()
            }}
            onChange={(e) => handleChange(e.target.checked, item)}
            checked={
              checkedItems!.findIndex((x) => x.boxNo === item.boxNo) >= 0
            }
          />
        )
      }

      if (selectionType === 'radio') {
        return <Radio></Radio>
      }
    }

    return (
      <span
        className={
          item.favorite ? `${styles.star} ${styles.light}` : styles.star
        }
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onFavorite?.(item, !item.favorite)
        }}
      >
        <StarOutlined />
      </span>
    )
  }

  const getConnIcon = (item: BoxItem): ReactNode => {
    const style = {
      fontSize: 23,
      color: item.connectionState == 1 ? '#1afa29' : '#a8a9a9'
    }
    if (item.comMode == 1) {
      return <UsbOutlined style={style} />
    }

    if (item.comMode == 2) {
      return (
        <Tooltip title={item.natIp}>
          <ApiOutlined style={style} />
        </Tooltip>
      )
    }

    return <UsbOutlined style={style} />
  }

  const handleItemClick = (item: BoxItem) => {
    if (item !== selectItem) {
      setSelectItem(item)
      onSelect?.(item)
    }
  }

  const handleMenuItemClick = (key: string, item: BoxItem) => {
    onMenuItemClick?.(key, item)
  }

  useEffect(() => {
    if (props.selectable !== true) {
      onCheck?.([])
    }
  }, [props.selectable])

  useEffect(() => {
    if (data && data.length > 0) {
      if (selectItem) {
        const index = data.findIndex((x) => x.boxNo === selectItem.boxNo)
        // 找不到当前设备时取上一个设备
        if (index < 0) {
          setSelectItem(data[data.length - 1])
          onSelect?.(data[data.length - 1])
        } else {
          // 重新设置当前选中设备，防止选中设备发生状态变更
          setSelectItem(data[index])
          onSelect?.(data[index])
        }
      } else {
        setSelectItem(data[0])
        onSelect?.(data[0])
      }
    } else {
      if (selectItem) {
        setSelectItem(undefined)
        onSelect?.(undefined)
      }
    }
  }, [data])

  useEffect(() => {
    if (checkAll !== undefined) {
      if (checkAll) {
        const items = props.data || []
        onCheck?.(items)
      } else {
        const items = []
        onCheck?.(items)
      }
    }
  }, [checkAll])

  return (
    <List
      header={header}
      itemLayout='horizontal'
      dataSource={props.data}
      className='box-list'
      renderItem={(item: BoxItem) => (
        <List.Item
          style={{ padding: '10px 10px' }}
          className={
            item.boxNo === selectItem?.boxNo
              ? 'box-list-item box-list-item-selected'
              : 'box-list-item'
          }
          onClick={() => handleItemClick(item)}
        >
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  key='box.list.context.edit'
                  onClick={(e) => {
                    e.domEvent.stopPropagation()
                    handleMenuItemClick('edit', item)
                  }}
                >
                  {formatMessage({ id: 'app.box.list.context.edit' })}
                </Menu.Item>
                {/* <Menu.Item
                  key='box.list.context.favorite'
                  onClick={(e) => {
                    e.domEvent.stopPropagation()
                    handleMenuItemClick('box.list.context.favorite', item)
                  }}
                >
                  {formatMessage({ id: 'app.box.list.context.favorite' })}
                </Menu.Item> */}
                <Menu.Item
                  key='box.list.context.delete'
                  onClick={(e) => {
                    e.domEvent.stopPropagation()
                    handleMenuItemClick('delete', item)
                  }}
                >
                  {formatMessage({ id: 'app.box.list.context.delete' })}
                </Menu.Item>
                <Menu.SubMenu
                  key='box.list.context.sub'
                  title={formatMessage({ id: 'app.box.list.context.sub' })}
                  onTitleClick={(e) => e.domEvent.stopPropagation()}
                >
                  <Menu.Item
                    key='box.list.context.firmware'
                    disabled={item.connectionState !== 1}
                    onClick={(e) => {
                      e.domEvent.stopPropagation()
                      handleMenuItemClick('firmware', item)
                    }}
                  >
                    {formatMessage({ id: 'app.box.list.context.firmware' })}
                  </Menu.Item>
                  <Menu.Item
                    key='box.list.context.template'
                    disabled={item.connectionState !== 1}
                    onClick={(e) => {
                      e.domEvent.stopPropagation()
                      handleMenuItemClick('template', item)
                    }}
                  >
                    {formatMessage({ id: 'app.box.list.context.template' })}
                  </Menu.Item>
                  <Menu.Item
                    key='box.list.context.clean'
                    disabled={item.connectionState !== 1}
                    onClick={(e) => {
                      e.domEvent.stopPropagation()
                      handleMenuItemClick('clean', item)
                    }}
                  >
                    {formatMessage({ id: 'app.box.list.context.clean' })}
                  </Menu.Item>
                  <Menu.Item
                    key='box.list.context.reboot'
                    disabled={item.connectionState !== 1}
                    onClick={(e) => {
                      e.domEvent.stopPropagation()
                      handleMenuItemClick('reboot', item)
                    }}
                  >
                    {formatMessage({ id: 'app.box.list.context.reboot' })}
                  </Menu.Item>
                  {/* <Menu.Item
                    key='box.list.context.reset'
                    onClick={(e) => {
                      e.domEvent.stopPropagation()
                      handleMenuItemClick('box.list.context.reset', item)
                    }}
                  >
                    {formatMessage({ id: 'app.box.list.context.reset' })}
                  </Menu.Item> */}
                </Menu.SubMenu>
              </Menu>
            }
            trigger={['contextMenu']}
          >
            <List.Item.Meta
              avatar={avatar(item)}
              title={item.boxNo}
              description={item.alias}
            />
          </Dropdown>
          {getConnIcon(item)}
        </List.Item>
      )}
    />
  )
}
